import { Table, Card, CardBody } from "reactstrap";
import React, { useEffect, useState } from "react";
import useSWR from "swr";
import { BsCheckCircle } from "react-icons/bs";
import { MdDragIndicator, MdKeyboardArrowUp, MdKeyboardArrowDown } from "react-icons/md";
import { ReactComponent as CrossIcon } from "../../../../assets/utils/images/svgs/Cross.svg";
import { useNavigate } from "react-router";
import {
    DndContext,
    pointerWithin,
    PointerSensor,
    KeyboardSensor,
    useSensor,
    useSensors,
} from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy, useSortable, arrayMove } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import ContractTypenService from "../../../../Api/Instellingen/ContractTypen/index";

const ROLE_CENTRAL = 0;
const ROLE_SCHOOL = 1;

const pick = (...vals) => vals.find((v) => v !== undefined && v !== null);


const mapCentral = (c, index) => ({
    id: c.ctid,
    name: c.name,
    abbr: c.abbr,
    cdAbbr: c.cdAbbr,
    visible: !pick(c.hidden, c.cp?.hidden, false),
    autoLoad: !pick(c.iAutoLoad, c.cp?.iAutoLoad, false),
    order: pick(c.order, c.cp?.order, index),
});

const mapSchool = (c, index) => ({
    id: c.ctid,
    name: c.name,
    abbr: c.abbr,
    cdAbbr: c.cdAbbr,
    visible: !pick(c.m_hidden, c.hidden, c.sp?.m_hidden, false),
    autoLoad: !pick(c.m_iAutoLoad, c.iAutoLoad, c.sp?.m_iAutoLoad, false),
    order: pick(c.m_order, c.order, c.sp?.m_order, index),
});

const StatusIcon = ({ ok }) =>
    ok ? (
        <BsCheckCircle color="#39A825" fontSize="18px" className="weekstaten-check-icon" />
    ) : (
        <CrossIcon />
    );

// ---------- one sortable row ----------
const SortableRow = ({ row, index, total, onMove, onOpen, disabled }) => {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
        id: row.id,
        disabled,
    });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.6 : 1,
        background: isDragging ? "#f1f5f9" : undefined,
    };

    return (
        <tr ref={setNodeRef} style={style}>
            <td style={{ width: 32 }}>
                <span
                    {...attributes}
                    {...listeners}
                    style={{ cursor: disabled ? "not-allowed" : "grab", touchAction: "none" }}
                >
                    <MdDragIndicator size={18} />
                </span>
            </td>
            <td className="text-center">{index + 1}</td>
            <td className="text-left svg-pointer" onClick={() => onOpen(row.id)}>
                {row.name}
            </td>
            <td className="text-left">{row.abbr}</td>
            <td>{row.cdAbbr}</td>
            <td className="text-center">
                <StatusIcon ok={row.visible} />
            </td>
            <td className="text-center">
                <StatusIcon ok={row.autoLoad} />
            </td>
            <td className="text-center">
                <div className="d-flex justify-content-center">
                    <button
                        type="button"
                        className="btn btn-link p-0 me-3"
                        disabled={disabled || index === 0}
                        onClick={() => onMove(index, index - 1)}
                    >
                        <MdKeyboardArrowUp size={20} />
                    </button>
                    <button
                        type="button"
                        className="btn btn-link p-0"
                        disabled={disabled || index === total - 1}
                        onClick={() => onMove(index, index + 1)}
                    >
                        <MdKeyboardArrowDown size={20} />
                    </button>
                </div>
            </td>
        </tr>
    );
};

// ---------- reusable card + table (used for both Centrale and School) ----------
const OrderTable = ({ title, rows, onReorder, onOpen, disabled }) => {
    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
        useSensor(KeyboardSensor) // Esc cancels a drag
    );

    const handleDragEnd = ({ active, over }) => {
        // dropped outside the rows (or on itself) → keep the same order, no API call
        if (!over || active.id === over.id) return;

        const from = rows.findIndex((r) => r.id === active.id);
        const to = rows.findIndex((r) => r.id === over.id);
        if (from < 0 || to < 0) return;

        onReorder(arrayMove(rows, from, to));
    };

    const move = (from, to) => onReorder(arrayMove(rows, from, to));

    return (
        <Card className="main-card mb-4">
            <CardBody className="p-xl-4 p-3 table-responsive">
                <div className="btn-color-style travelCost-blue-text mb-2">{title}</div>
                <Table hover size="sm" className="mb-0 coor-verzoeken-table contract-typen-table">
                    <thead>
                        <tr>
                            <th></th>
                            <th className="text-center">Positie</th>
                            <th className="text-left">Contracttype</th>
                            <th className="text-left">Afkorting</th>
                            <th>Dagtype</th>
                            <th className="text-center">Weergeven</th>
                            <th className="text-center">Autoload</th>
                            <th className="text-center">Verplaatsen</th>
                        </tr>
                    </thead>
                    <DndContext
                        sensors={sensors}
                        collisionDetection={pointerWithin}
                        onDragEnd={handleDragEnd}
                    >
                        <SortableContext items={rows.map((r) => r.id)} strategy={verticalListSortingStrategy}>
                            <tbody>
                                {rows.length ? (
                                    rows.map((row, index) => (
                                        <SortableRow
                                            key={row.id}
                                            row={row}
                                            index={index}
                                            total={rows.length}
                                            onMove={move}
                                            onOpen={onOpen}
                                            disabled={disabled}
                                        />
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan={8} className="text-center">
                                            Geen data beschikbaar
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </SortableContext>
                    </DndContext>
                </Table>
            </CardBody>
        </Card>
    );
};

// ---------- parent ----------
const ContractTypenTable = ({ communityId }) => {
    const navigate = useNavigate();
    const [central, setCentral] = useState([]);
    const [school, setSchool] = useState([]);
    const [saving, setSaving] = useState(false);

    // SWR fetch: refetches automatically when communityId changes
    const { data: contractData, mutate } = useSWR(
        communityId ? `contracts-${communityId}` : null,
        () => ContractTypenService.fetchContracts(communityId).then((res) => res?.data?.data),
        { revalidateOnFocus: false }
    );

    // copy server data into local state (so rows can be reordered instantly)
    useEffect(() => {
        const cp = Array.isArray(contractData?.cp) ? contractData.cp : [];
        const sp = Array.isArray(contractData?.sp) ? contractData.sp : [];

        setCentral(cp.map(mapCentral).sort((a, b) => a.order - b.order));
        setSchool(sp.map(mapSchool).sort((a, b) => a.order - b.order));
    }, [contractData]);

    const openContract = (id) =>
        navigate(`/c-instellingen/contracttypen/wijzigen-toevoegen/${id}`, {
            state: { contractId: id, communityId },
        });

    const saveOrder = async (role, newRows, oldRows, setter) => {
        if (saving) return;

        setter(newRows); // update the UI immediately
        setSaving(true);

        try {
            await ContractTypenService.updateContractOrder({
                iCommunityId: Number(communityId),
                role,
                contractIds: newRows.map((r) => r.id),
            });
            mutate(); // refetch so the table matches the backend
        } catch (e) {
            setter(oldRows); // roll back if the save fails
            // show your usual error toast here
        } finally {
            setSaving(false);
        }
    };

    return (
        <>
            <OrderTable
                title="Centrale planning"
                rows={central}
                disabled={saving}
                onOpen={openContract}
                onReorder={(newRows) => saveOrder(ROLE_CENTRAL, newRows, central, setCentral)}
            />
            <OrderTable
                title="Schoolplanning"
                rows={school}
                disabled={saving}
                onOpen={openContract}
                onReorder={(newRows) => saveOrder(ROLE_SCHOOL, newRows, school, setSchool)}
            />
        </>
    );
};

export default ContractTypenTable;